import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

interface CallState {
  isInCall: boolean;
  isRinging: boolean;
  remoteUser: {
    id: string;
    name: string;
    photo?: string;
  } | null;
  error: string | null;
}

export const useCall = () => {
  const [state, setState] = useState<CallState>({
    isInCall: false,
    isRinging: false,
    remoteUser: null,
    error: null
  });

  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);

  useEffect(() => {
    setupWebSocket();
    return () => {
      if (peerConnection) {
        peerConnection.close();
      }
    };
  }, []);

  const setupWebSocket = () => {
    const ws = new WebSocket(process.env.REACT_APP_WS_URL || 'wss://lavernaibackend.onrender.com');
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'call:ringing':
          handleIncomingCall(data.payload);
          break;
        case 'call:offer':
          handleCallOffer(data.payload);
          break;
        case 'call:answer':
          handleCallAnswer(data.payload);
          break;
        case 'call:ice-candidate':
          handleIceCandidate(data.payload);
          break;
        case 'call:ended':
          handleCallEnded();
          break;
      }
    };

    return () => {
      ws.close();
    };
  };

  const handleIncomingCall = (data: any) => {
    setState(prev => ({
      ...prev,
      isRinging: true,
      remoteUser: {
        id: data.callerId,
        name: data.callerName,
        photo: data.callerPhoto
      }
    }));
  };

  const handleCallOffer = async (data: any) => {
    try {
      const pc = new RTCPeerConnection();
      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      setPeerConnection(pc);
      setState(prev => ({
        ...prev,
        isInCall: true,
        isRinging: false
      }));

      // Send answer to caller
      api.post('/api/calls/answer', {
        callerId: data.callerId,
        answer
      });
    } catch (error) {
      console.error('Error handling call offer:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to handle call'
      }));
    }
  };

  const handleCallAnswer = async (data: any) => {
    if (peerConnection) {
      try {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
        setState(prev => ({
          ...prev,
          isInCall: true,
          isRinging: false
        }));
      } catch (error) {
        console.error('Error handling call answer:', error);
      }
    }
  };

  const handleIceCandidate = async (data: any) => {
    if (peerConnection) {
      try {
        await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
      } catch (error) {
        console.error('Error handling ICE candidate:', error);
      }
    }
  };

  const handleCallEnded = () => {
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }
    setState({
      isInCall: false,
      isRinging: false,
      remoteUser: null,
      error: null
    });
  };

  const acceptCall = useCallback(async () => {
    if (!state.remoteUser) return;

    try {
      const pc = new RTCPeerConnection();
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      setPeerConnection(pc);
      
      await api.post('/api/calls/accept', {
        recipientId: state.remoteUser.id,
        offer
      });
    } catch (error) {
      console.error('Error accepting call:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to accept call'
      }));
    }
  }, [state.remoteUser]);

  const declineCall = useCallback(async () => {
    if (!state.remoteUser) return;

    try {
      await api.post('/api/calls/decline', {
        callerId: state.remoteUser.id
      });
      handleCallEnded();
    } catch (error) {
      console.error('Error declining call:', error);
    }
  }, [state.remoteUser]);

  const endCall = useCallback(async () => {
    if (!state.remoteUser) return;

    try {
      await api.post('/api/calls/end', {
        recipientId: state.remoteUser.id
      });
      handleCallEnded();
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }, [state.remoteUser]);

  return {
    ...state,
    acceptCall,
    declineCall,
    endCall
  };
}; 